import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { AuthenticationService } from './auth.service';
import Swal from 'sweetalert2';

@Injectable({ providedIn: 'root' })
export class IdleTimerService implements OnDestroy {
  // Total inactivity before user is logged out. Modal pops `LOGOUT_COUNTDOWN_MS` before this.
  private readonly TOTAL_IDLE_TIMEOUT_MS = 60 * 60 * 1000; // 60 minutes
  private readonly LOGOUT_COUNTDOWN_MS = 5 * 60 * 1000; // 5 minutes
  private readonly IDLE_TIMEOUT_MS = this.TOTAL_IDLE_TIMEOUT_MS - this.LOGOUT_COUNTDOWN_MS;

  private idleTimer: any;
  private listening = false;

  private readonly activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];

  constructor(
    private authService: AuthenticationService,
    private ngZone: NgZone
  ) {}

  start() {
    if (this.listening) return;
    this.listening = true;

    this.ngZone.runOutsideAngular(() => {
      this.activityEvents.forEach(event => {
        window.addEventListener(event, this.onActivity, { passive: true });
      });
    });

    this.resetIdleTimer();
  }

  stop() {
    this.listening = false;
    this.activityEvents.forEach(event => {
      window.removeEventListener(event, this.onActivity);
    });
    clearTimeout(this.idleTimer);
  }

  private onActivity = () => {
    if (!this.listening) return;
    this.resetIdleTimer();
  };

  private resetIdleTimer() {
    clearTimeout(this.idleTimer);

    this.idleTimer = setTimeout(() => {
      this.ngZone.run(() => this.showWarning());
    }, this.IDLE_TIMEOUT_MS);
  }

  private showWarning() {
    let countdownInterval: any;

    Swal.fire({
      title: 'Session Timeout',
      html: `Your session is about to expire due to inactivity.<br>You will be logged out in <b>5:00</b>.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Stay Logged In',
      cancelButtonText: 'Log Out',
      allowOutsideClick: false,
      allowEscapeKey: false,
      timer: this.LOGOUT_COUNTDOWN_MS,
      timerProgressBar: true,
      customClass: {
        confirmButton: 'btn btn-success',
        cancelButton: 'btn btn-secondary ms-2'
      },
      buttonsStyling: false,
      didOpen: () => {
        const counter = Swal.getHtmlContainer()?.querySelector('b');
        countdownInterval = setInterval(() => {
          const remaining = Swal.getTimerLeft();
          if (counter && remaining != null) {
            const totalSeconds = Math.max(0, Math.ceil(remaining / 1000));
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;
            counter.textContent = `${minutes}:${seconds.toString().padStart(2, '0')}`;
          }
        }, 250);
      },
      willClose: () => {
        clearInterval(countdownInterval);
      }
    }).then((result) => {
      if (result.isConfirmed) {
        this.authService.refreshToken().catch(() => {
          // silent refresh failed (e.g. B2C refresh token expired) — log out
          this.authService.logout();
        });
        this.resetIdleTimer();
      } else {
        this.authService.logout();
      }
    });
  }

  ngOnDestroy() {
    this.stop();
  }
}
